/**
 * Outing Setup Wizard - Step-by-step outing creation
 * Guided flow for organizers to set up a complete golf outing.
 * Includes: Course lookup, Split format (Front 9 / Back 9), Par confirmation
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, ChevronRight, Check, Calendar, Users, 
  Settings, DollarSign, Eye, MapPin, Clock, Search, Edit2
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Switch } from '../../../components/ui/switch';
import { Slider } from '../../../components/ui/slider';
import { Badge } from '../../../components/ui/badge';
import { toast } from 'sonner';
import { CourseLookupModal } from '../../../components/score-entry';
import { useOutingApi } from '../hooks/useOutingApi';
import { GAME_FORMATS } from '../utils/outingHelpers';

const STEPS = [
  { id: 'details', label: 'Event Details', icon: Calendar },
  { id: 'course', label: 'Course & Pars', icon: MapPin },
  { id: 'game', label: 'Game Format', icon: Settings },
  { id: 'payments', label: 'Entry Fee', icon: DollarSign },
  { id: 'review', label: 'Review', icon: Eye }
];

// Default pars if no course selected
const DEFAULT_PARS = [4, 4, 3, 4, 5, 4, 3, 4, 5, 4, 4, 3, 4, 5, 4, 3, 4, 5];

export default function OutingSetupWizard({ user }) {
  const navigate = useNavigate();
  const api = useOutingApi();
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [showCourseLookup, setShowCourseLookup] = useState(false);
  const [editingPars, setEditingPars] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    // Event details
    name: '',
    description: '',
    event_date: '',
    tee_time: '08:00',
    max_players: 80,
    
    // Course data
    course_name: '',
    course_id: null,
    hole_pars: [...DEFAULT_PARS],
    
    // Game settings - now with split format support
    game_settings: {
      // Front 9 format
      front_9_format: 'scramble',
      // Back 9 format (can be different)
      back_9_format: 'scramble',
      // Use same format for both?
      same_format_all: true,
      
      team_size: 4,
      
      skins: {
        enabled: false,
        total_pot: 90, // Total pot, divided by 18 holes
        carryover: true
      },
      ctp: {
        enabled: true,
        holes: [], // Will be auto-populated with par 3s
        total_pot: 100 // Total CTP pot, divided among par 3 holes
      },
      custom_rules: ''
    },
    
    // Entry fee
    entry_fee: 150
  });

  // Auto-select par 3 holes for CTP when pars change
  useEffect(() => {
    const par3Holes = formData.hole_pars
      .map((par, i) => par === 3 ? i + 1 : null)
      .filter(h => h !== null);
    
    setFormData(prev => ({
      ...prev,
      game_settings: {
        ...prev.game_settings,
        ctp: {
          ...prev.game_settings.ctp,
          holes: par3Holes
        }
      }
    }));
  }, [formData.hole_pars]);

  const updateForm = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const updateGameSettings = (field, value) => {
    setFormData(prev => ({
      ...prev,
      game_settings: {
        ...prev.game_settings,
        [field]: value
      }
    }));
  };

  const updateSkins = (field, value) => {
    setFormData(prev => ({
      ...prev,
      game_settings: {
        ...prev.game_settings,
        skins: {
          ...prev.game_settings.skins,
          [field]: value
        }
      }
    }));
  };

  const updateCTP = (field, value) => {
    setFormData(prev => ({
      ...prev,
      game_settings: {
        ...prev.game_settings,
        ctp: {
          ...prev.game_settings.ctp,
          [field]: value
        }
      }
    }));
  };

  const updateHolePar = (holeIndex, par) => {
    const newPars = [...formData.hole_pars];
    newPars[holeIndex] = par;
    updateForm('hole_pars', newPars);
  };

  const toggleCTPHole = (hole) => {
    const currentHoles = formData.game_settings.ctp.holes;
    if (currentHoles.includes(hole)) {
      updateCTP('holes', currentHoles.filter(h => h !== hole));
    } else {
      updateCTP('holes', [...currentHoles, hole].sort((a, b) => a - b));
    }
  };

  const handleCourseSelect = (course) => {
    console.log('Course selected:', course);
    
    // Extract hole pars from course data
    let holePars = [...DEFAULT_PARS];
    
    if (course.hole_pars && course.hole_pars.length === 18) {
      holePars = course.hole_pars;
    } else if (course.holes && course.holes.length === 18) {
      holePars = course.holes.map(h => h.par || 4);
    }
    
    setFormData(prev => ({
      ...prev,
      course_name: course.name || course.course_name || '',
      course_id: course.course_id || course.id || null,
      hole_pars: holePars
    }));
    
    setShowCourseLookup(false);
    toast.success(`Course selected: ${course.name || course.course_name}`);
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: // Details
        return formData.name && formData.event_date;
      case 1: // Course
        return formData.course_name || formData.hole_pars.length === 18;
      case 2: // Game
        return true;
      case 3: // Payments
        return true;
      case 4: // Review
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      // Prepare game settings - convert split format to API format
      const gameSettings = {
        ...formData.game_settings,
        // Primary format (used for display)
        format: formData.game_settings.front_9_format,
        // Store both formats
        front_9_format: formData.game_settings.front_9_format,
        back_9_format: formData.game_settings.same_format_all 
          ? formData.game_settings.front_9_format 
          : formData.game_settings.back_9_format
      };
      
      const outingData = {
        name: formData.name,
        description: formData.description,
        course_name: formData.course_name,
        course_id: formData.course_id,
        hole_pars: formData.hole_pars,
        event_date: new Date(formData.event_date).toISOString(),
        tee_time: formData.tee_time,
        max_players: formData.max_players,
        entry_fee: formData.entry_fee,
        game_settings: gameSettings
      };
      
      const result = await api.createOuting(
        outingData,
        user.user_id,
        user.display_name || user.email
      );
      
      if (result.success) {
        toast.success('Outing created successfully!');
        navigate(`/outings/${result.outing.id}`);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to create outing');
    } finally {
      setSaving(false);
    }
  };

  // Calculate totals
  const frontNinePar = formData.hole_pars.slice(0, 9).reduce((a, b) => a + b, 0);
  const backNinePar = formData.hole_pars.slice(9, 18).reduce((a, b) => a + b, 0);
  const totalPar = frontNinePar + backNinePar;

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return renderEventDetails();
      case 1:
        return renderCourseSetup();
      case 2:
        return renderGameSetup();
      case 3:
        return renderPaymentSetup();
      case 4:
        return renderReview();
      default:
        return null;
    }
  };

  const renderEventDetails = () => (
    <div className="space-y-6">
      <div>
        <Label htmlFor="name" className="text-white">Event Name *</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => updateForm('name', e.target.value)}
          placeholder="e.g., Annual Charity Golf Classic"
          className="bg-[#1A1A1A] border-white/20 text-white mt-2"
          data-testid="outing-name-input"
        />
      </div>
      
      <div>
        <Label htmlFor="description" className="text-white">Description</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => updateForm('description', e.target.value)}
          placeholder="Tell players what this event is about..."
          className="bg-[#1A1A1A] border-white/20 text-white mt-2 min-h-[100px]"
        />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="date" className="text-white">Event Date *</Label>
          <Input
            id="date"
            type="date"
            value={formData.event_date}
            onChange={(e) => updateForm('event_date', e.target.value)}
            className="bg-[#1A1A1A] border-white/20 text-white mt-2"
            data-testid="outing-date-input"
          />
        </div>
        
        <div>
          <Label htmlFor="tee_time" className="text-white">Tee Time</Label>
          <Input
            id="tee_time"
            type="time"
            value={formData.tee_time}
            onChange={(e) => updateForm('tee_time', e.target.value)}
            className="bg-[#1A1A1A] border-white/20 text-white mt-2"
          />
        </div>
      </div>
      
      <div>
        <Label className="text-white">Max Players: {formData.max_players}</Label>
        <Slider
          value={[formData.max_players]}
          onValueChange={(v) => updateForm('max_players', v[0])}
          min={8}
          max={200}
          step={4}
          className="mt-4"
        />
      </div>
    </div>
  );

  const renderCourseSetup = () => (
    <div className="space-y-6">
      {/* Course Selection */}
      <div>
        <Label className="text-white mb-3 block">Golf Course *</Label>
        <Card 
          className={`bg-[#1A1A1A] border-white/10 cursor-pointer hover:border-[#007AFF]/50 transition-colors ${
            formData.course_name ? 'border-green-500/50' : ''
          }`}
          onClick={() => setShowCourseLookup(true)}
          data-testid="course-select-card"
        >
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                formData.course_name ? 'bg-green-500/20' : 'bg-[#007AFF]/20'
              }`}>
                {formData.course_name ? (
                  <Check className="w-6 h-6 text-green-400" />
                ) : (
                  <Search className="w-6 h-6 text-[#007AFF]" />
                )}
              </div>
              <div>
                {formData.course_name ? (
                  <>
                    <p className="font-semibold text-white">{formData.course_name}</p>
                    <p className="text-sm text-zinc-400">Par {totalPar} • Click to change</p>
                  </>
                ) : (
                  <>
                    <p className="font-semibold text-white">Search for Course</p>
                    <p className="text-sm text-zinc-400">Find your course to load hole pars</p>
                  </>
                )}
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-zinc-500" />
          </CardContent>
        </Card>
      </div>

      {/* Hole Pars Confirmation */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <Label className="text-white">Confirm Hole Pars</Label>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setEditingPars(!editingPars)}
            className="text-[#007AFF]"
          >
            <Edit2 className="w-4 h-4 mr-1" />
            {editingPars ? 'Done' : 'Edit'}
          </Button>
        </div>
        
        {/* Front 9 */}
        <Card className="bg-[#1A1A1A] border-white/10 mb-3">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-zinc-400 flex justify-between">
              <span>Front 9</span>
              <span className="text-white">Par {frontNinePar}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-9 gap-1">
              {formData.hole_pars.slice(0, 9).map((par, i) => (
                <div key={i} className="text-center">
                  <div className="text-xs text-zinc-500 mb-1">{i + 1}</div>
                  {editingPars ? (
                    <select
                      value={par}
                      onChange={(e) => updateHolePar(i, parseInt(e.target.value))}
                      className="w-full bg-[#0A0A0A] border border-white/20 rounded text-white text-center text-sm py-1"
                    >
                      <option value={3}>3</option>
                      <option value={4}>4</option>
                      <option value={5}>5</option>
                    </select>
                  ) : (
                    <div className={`py-1 rounded text-sm font-semibold ${
                      par === 3 ? 'bg-yellow-500/20 text-yellow-400' :
                      par === 5 ? 'bg-blue-500/20 text-blue-400' : 'bg-white/10 text-white'
                    }`}>
                      {par}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        
        {/* Back 9 */}
        <Card className="bg-[#1A1A1A] border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-zinc-400 flex justify-between">
              <span>Back 9</span>
              <span className="text-white">Par {backNinePar}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-9 gap-1">
              {formData.hole_pars.slice(9, 18).map((par, i) => (
                <div key={i + 9} className="text-center">
                  <div className="text-xs text-zinc-500 mb-1">{i + 10}</div>
                  {editingPars ? (
                    <select
                      value={par}
                      onChange={(e) => updateHolePar(i + 9, parseInt(e.target.value))}
                      className="w-full bg-[#0A0A0A] border border-white/20 rounded text-white text-center text-sm py-1"
                    >
                      <option value={3}>3</option>
                      <option value={4}>4</option>
                      <option value={5}>5</option>
                    </select>
                  ) : (
                    <div className={`py-1 rounded text-sm font-semibold ${
                      par === 3 ? 'bg-yellow-500/20 text-yellow-400' :
                      par === 5 ? 'bg-blue-500/20 text-blue-400' : 'bg-white/10 text-white'
                    }`}>
                      {par}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        
        <div className="text-center mt-3 text-sm text-zinc-400">
          Total Par: <span className="text-white font-bold">{totalPar}</span>
        </div>
      </div>
    </div>
  );

  const renderGameSetup = () => (
    <div className="space-y-6">
      {/* Same Format Toggle */}
      <div className="flex items-center justify-between p-4 bg-[#1A1A1A] rounded-lg border border-white/10">
        <div>
          <h4 className="font-semibold text-white">Same Format All 18</h4>
          <p className="text-xs text-zinc-400">Use one format for entire round</p>
        </div>
        <Switch
          checked={formData.game_settings.same_format_all}
          onCheckedChange={(v) => updateGameSettings('same_format_all', v)}
        />
      </div>

      {/* Front 9 Format */}
      <div>
        <Label className="text-white mb-3 block">
          {formData.game_settings.same_format_all ? 'Game Format' : 'Front 9 Format'}
        </Label>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(GAME_FORMATS).map(([key, format]) => (
            <Card
              key={key}
              className={`bg-[#1A1A1A] cursor-pointer transition-all ${
                formData.game_settings.front_9_format === key
                  ? 'border-[#007AFF] ring-1 ring-[#007AFF]'
                  : 'border-white/10 hover:border-white/30'
              }`}
              onClick={() => updateGameSettings('front_9_format', key)}
              data-testid={`front-format-${key}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">{format.icon}</span>
                  <h4 className="font-bold text-white">{format.name}</h4>
                </div>
                <p className="text-xs text-zinc-400">{format.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Back 9 Format (only if split) */}
      {!formData.game_settings.same_format_all && (
        <div>
          <Label className="text-white mb-3 block">Back 9 Format</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.entries(GAME_FORMATS).map(([key, format]) => (
              <Card
                key={key}
                className={`bg-[#1A1A1A] cursor-pointer transition-all ${
                  formData.game_settings.back_9_format === key
                    ? 'border-purple-500 ring-1 ring-purple-500'
                    : 'border-white/10 hover:border-white/30'
                }`}
                onClick={() => updateGameSettings('back_9_format', key)}
                data-testid={`back-format-${key}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">{format.icon}</span>
                    <h4 className="font-bold text-white">{format.name}</h4>
                  </div>
                  <p className="text-xs text-zinc-400">{format.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
      
      {/* Team Size */}
      <div>
        <Label className="text-white mb-3 block">Team Size</Label>
        <div className="flex gap-3">
          {[2, 4].map(size => (
            <Button
              key={size}
              variant={formData.game_settings.team_size === size ? 'default' : 'outline'}
              className={formData.game_settings.team_size === size ? 'bg-[#007AFF]' : ''}
              onClick={() => updateGameSettings('team_size', size)}
            >
              {size}-Man Teams
            </Button>
          ))}
        </div>
      </div>
      
      {/* Skins */}
      <Card className="bg-[#1A1A1A] border-white/10">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="font-semibold text-white">Skins Game</h4>
              <p className="text-xs text-zinc-400">Optional skins competition overlay</p>
            </div>
            <Switch
              checked={formData.game_settings.skins.enabled}
              onCheckedChange={(v) => updateSkins('enabled', v)}
            />
          </div>
          
          {formData.game_settings.skins.enabled && (
            <div className="space-y-4 pt-4 border-t border-white/10">
              <div>
                <Label className="text-white">Total Skins Pot: ${formData.game_settings.skins.total_pot}</Label>
                <Slider
                  value={[formData.game_settings.skins.total_pot]}
                  onValueChange={(v) => updateSkins('total_pot', v[0])}
                  min={18}
                  max={900}
                  step={18}
                  className="mt-2"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  = ${(formData.game_settings.skins.total_pot / 18).toFixed(2)} per hole
                </p>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-400">Carryover on ties</span>
                <Switch
                  checked={formData.game_settings.skins.carryover}
                  onCheckedChange={(v) => updateSkins('carryover', v)}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Closest to Pin */}
      <Card className="bg-[#1A1A1A] border-white/10">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="font-semibold text-white">Closest to Pin Contest</h4>
              <p className="text-xs text-zinc-400">Par 3 holes auto-selected</p>
            </div>
            <Switch
              checked={formData.game_settings.ctp.enabled}
              onCheckedChange={(v) => updateCTP('enabled', v)}
            />
          </div>
          
          {formData.game_settings.ctp.enabled && (
            <div className="space-y-4 pt-4 border-t border-white/10">
              <div>
                <Label className="text-white mb-2 block">CTP Holes (Par 3s)</Label>
                <div className="flex flex-wrap gap-2">
                  {formData.hole_pars.map((par, i) => {
                    const hole = i + 1;
                    const isPar3 = par === 3;
                    const isSelected = formData.game_settings.ctp.holes.includes(hole);
                    
                    if (!isPar3) return null;
                    
                    return (
                      <Button
                        key={hole}
                        size="sm"
                        variant={isSelected ? 'default' : 'outline'}
                        className={isSelected ? 'bg-yellow-500 text-black' : 'border-yellow-500/50 text-yellow-400'}
                        onClick={() => toggleCTPHole(hole)}
                      >
                        Hole {hole}
                      </Button>
                    );
                  })}
                </div>
              </div>
              
              <div>
                <Label className="text-white">Total CTP Prize Pot: ${formData.game_settings.ctp.total_pot}</Label>
                <Slider
                  value={[formData.game_settings.ctp.total_pot]}
                  onValueChange={(v) => updateCTP('total_pot', v[0])}
                  min={0}
                  max={500}
                  step={25}
                  className="mt-2"
                />
                {formData.game_settings.ctp.holes.length > 0 && (
                  <p className="text-xs text-zinc-500 mt-1">
                    = ${(formData.game_settings.ctp.total_pot / formData.game_settings.ctp.holes.length).toFixed(2)} per CTP hole
                  </p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Custom Rules */}
      <div>
        <Label htmlFor="rules" className="text-white">Custom Rules / Notes</Label>
        <Textarea
          id="rules"
          value={formData.game_settings.custom_rules}
          onChange={(e) => updateGameSettings('custom_rules', e.target.value)}
          placeholder="Any special rules or instructions for players..."
          className="bg-[#1A1A1A] border-white/20 text-white mt-2 min-h-[80px]"
        />
      </div>
    </div>
  );

  const renderPaymentSetup = () => (
    <div className="space-y-6">
      <div>
        <Label className="text-white mb-2 block">Entry Fee Per Player</Label>
        <div className="flex items-center gap-4">
          <span className="text-4xl font-black text-[#007AFF]">
            ${formData.entry_fee}
          </span>
        </div>
        <Slider
          value={[formData.entry_fee]}
          onValueChange={(v) => updateForm('entry_fee', v[0])}
          min={0}
          max={500}
          step={5}
          className="mt-4"
        />
        <p className="text-xs text-zinc-400 mt-2">
          Set to $0 for free events
        </p>
      </div>
      
      <Card className="bg-[#1A1A1A] border-white/10">
        <CardContent className="p-4">
          <h4 className="font-semibold text-white mb-4">Estimated Revenue</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-zinc-400">Max Players</span>
              <span className="text-white">{formData.max_players}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-400">Entry Fee</span>
              <span className="text-white">${formData.entry_fee}</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-white/10">
              <span className="text-zinc-400">Potential Total</span>
              <span className="text-2xl font-bold text-green-400">
                ${formData.max_players * formData.entry_fee}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
        <h4 className="font-semibold text-blue-400 mb-2">Payment Tracking</h4>
        <p className="text-sm text-zinc-300">
          You&apos;ll be able to track payments via Zelle, Venmo, Cash, or Card. 
          Mark players as paid as you receive payments.
        </p>
      </div>
    </div>
  );

  const renderReview = () => {
    const front9Format = GAME_FORMATS[formData.game_settings.front_9_format];
    const back9Format = GAME_FORMATS[formData.game_settings.back_9_format];
    
    return (
      <div className="space-y-6">
        <Card className="bg-[#1A1A1A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white">Event Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-zinc-400">Event Name</p>
                <p className="text-white font-semibold">{formData.name || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-zinc-400">Course</p>
                <p className="text-white font-semibold">{formData.course_name || 'Manual pars set'}</p>
              </div>
              <div>
                <p className="text-xs text-zinc-400">Date</p>
                <p className="text-white font-semibold">
                  {formData.event_date ? new Date(formData.event_date).toLocaleDateString() : '—'}
                </p>
              </div>
              <div>
                <p className="text-xs text-zinc-400">Tee Time</p>
                <p className="text-white font-semibold">{formData.tee_time}</p>
              </div>
              <div>
                <p className="text-xs text-zinc-400">Max Players</p>
                <p className="text-white font-semibold">{formData.max_players}</p>
              </div>
              <div>
                <p className="text-xs text-zinc-400">Entry Fee</p>
                <p className="text-white font-semibold">${formData.entry_fee}</p>
              </div>
              <div>
                <p className="text-xs text-zinc-400">Course Par</p>
                <p className="text-white font-semibold">{totalPar}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[#1A1A1A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white">Game Format</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {formData.game_settings.same_format_all ? (
              <div className="flex items-center gap-3">
                <span className="text-2xl">{front9Format?.icon}</span>
                <div>
                  <p className="font-semibold text-white">{front9Format?.name}</p>
                  <p className="text-xs text-zinc-400">All 18 holes</p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{front9Format?.icon}</span>
                  <div>
                    <p className="font-semibold text-white">{front9Format?.name}</p>
                    <p className="text-xs text-zinc-400">Front 9 (Holes 1-9)</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{back9Format?.icon}</span>
                  <div>
                    <p className="font-semibold text-purple-400">{back9Format?.name}</p>
                    <p className="text-xs text-zinc-400">Back 9 (Holes 10-18)</p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex gap-2 pt-2">
              <Badge variant="outline">{formData.game_settings.team_size}-Man Teams</Badge>
            </div>
            
            {formData.game_settings.skins.enabled && (
              <p className="text-sm text-zinc-300">
                Skins: ${formData.game_settings.skins.total_pot} total pot 
                (${(formData.game_settings.skins.total_pot / 18).toFixed(2)}/hole)
                {formData.game_settings.skins.carryover ? ' w/ carryover' : ''}
              </p>
            )}
            
            {formData.game_settings.ctp.enabled && (
              <p className="text-sm text-zinc-300">
                Closest to Pin: Holes {formData.game_settings.ctp.holes.join(', ')} 
                (${formData.game_settings.ctp.total_pot} prize pot)
              </p>
            )}
            
            {formData.game_settings.custom_rules && (
              <div className="pt-2 border-t border-white/10">
                <p className="text-xs text-zinc-400">Custom Rules</p>
                <p className="text-sm text-white">{formData.game_settings.custom_rules}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="ps-theme-page min-h-screen p-4 pb-24" data-testid="outing-setup-theme-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/outings')}
          className="text-zinc-400 hover:text-white"
        >
          <ChevronLeft className="w-6 h-6" />
        </Button>
        <div>
          <h1 className="text-2xl font-black tracking-tight uppercase">Create Outing</h1>
          <p className="text-sm text-zinc-400">Step {currentStep + 1} of {STEPS.length}</p>
        </div>
      </div>
      
      {/* Progress Steps */}
      <div className="flex items-center justify-between mb-8 overflow-x-auto pb-2">
        {STEPS.map((step, index) => {
          const Icon = step.icon;
          const isActive = index === currentStep;
          const isCompleted = index < currentStep;
          
          return (
            <div 
              key={step.id}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors whitespace-nowrap ${
                isActive ? 'bg-[#007AFF]/20 text-[#007AFF]' : 
                isCompleted ? 'text-green-400' : 'text-zinc-500'
              }`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                isActive ? 'bg-[#007AFF]' : 
                isCompleted ? 'bg-green-500' : 'bg-zinc-700'
              }`}>
                {isCompleted ? <Check className="w-4 h-4 text-white" /> : <Icon className="w-4 h-4 text-white" />}
              </div>
              <span className="text-sm font-medium hidden md:inline">{step.label}</span>
            </div>
          );
        })}
      </div>
      
      {/* Step Content */}
      <Card className="bg-[#121212] border-white/10 mb-6">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            {React.createElement(STEPS[currentStep].icon, { className: 'w-5 h-5 text-[#007AFF]' })}
            {STEPS[currentStep].label}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {renderStepContent()}
        </CardContent>
      </Card>
      
      {/* Navigation */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={handleBack}
          disabled={currentStep === 0}
          className="border-white/20"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        
        {currentStep < STEPS.length - 1 ? (
          <Button
            onClick={handleNext}
            disabled={!canProceed()}
            className="bg-[#007AFF] hover:bg-[#38BDF8]"
            data-testid="wizard-next-btn"
          >
            Next
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={saving || !canProceed()}
            className="bg-green-600 hover:bg-green-500"
            data-testid="wizard-create-btn"
          >
            {saving ? 'Creating...' : 'Create Outing'}
            <Check className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>
      
      {/* Course Lookup Modal */}
      <CourseLookupModal
        isOpen={showCourseLookup}
        onClose={() => setShowCourseLookup(false)}
        onCourseSelect={handleCourseSelect}
        currentCourseName={formData.course_name}
        title="Find Your Course"
      />
    </div>
  );
}
